import React from 'react';
import { Descriptions, Drawer, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FC120Detail, FC121Detail } from '../../../services/c504Api';

const { Text } = Typography;

function renderApprovalStat(stat: string): React.ReactNode {
  if (stat === 'D') return <Tag color="orange">送審中</Tag>;
  if (stat === 'P') return <Tag color="green">已核准</Tag>;
  if (stat === 'N') return <Tag color="red">已駁回</Tag>;
  return <Tag color="default">{stat || '—'}</Tag>;
}

function formatNumber(value: number, decimals = 2): string {
  if (!value && value !== 0) return '—';
  return value.toLocaleString('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatDate(date: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('zh-TW');
}

interface DetailDrawerProps {
  visible: boolean;
  loading: boolean;
  data: FC120Detail | null;
  onClose: () => void;
}

const detailColumns: ColumnsType<FC121Detail> = [
  { title: '項次', dataIndex: 'fc121_02', key: 'seq', width: 60, align: 'center' },
  { title: '產品代號', dataIndex: 'fc121_11', key: 'product', width: 120 },
  { title: '色號', dataIndex: 'fc121_12', key: 'color', width: 80 },
  { title: '規格', dataIndex: 'fc121_13', key: 'spec', ellipsis: true },
  { title: '單位', dataIndex: 'fc121_15', key: 'unit', width: 60, align: 'center' },
  {
    title: '數量',
    dataIndex: 'fc121_16',
    key: 'qty',
    width: 100,
    align: 'right',
    render: (v: number) => formatNumber(v),
  },
  {
    title: '單價',
    dataIndex: 'fc121_18',
    key: 'price',
    width: 100,
    align: 'right',
    render: (v: number) => formatNumber(v),
  },
  {
    title: '金額',
    dataIndex: 'fc121_19',
    key: 'amount',
    width: 120,
    align: 'right',
    render: (v: number) => formatNumber(v),
  },
];

const DetailDrawer: React.FC<DetailDrawerProps> = ({ visible, loading, data, onClose }) => (
  <Drawer
    title={data ? `交運單明細 — ${data.fc120_01}` : '交運單明細'}
    placement="right"
    width={720}
    open={visible}
    onClose={onClose}
  >
    {loading ? (
      <Spin tip="載入中..." style={{ display: 'block', marginTop: 100 }} />
    ) : data ? (
      <>
        <div className="c504-detail-section">
          <h4>單頭資訊</h4>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="交運單號">{data.fc120_01}</Descriptions.Item>
            <Descriptions.Item label="S/C No">{data.fc120_10}</Descriptions.Item>
            <Descriptions.Item label="交運日期">{formatDate(data.fc120_11)}</Descriptions.Item>
            <Descriptions.Item label="客戶代號">{data.fc120_12}</Descriptions.Item>
            <Descriptions.Item label="部門代號">{data.fc120_17}</Descriptions.Item>
            <Descriptions.Item label="幣別">{data.fc120_18}</Descriptions.Item>
            <Descriptions.Item label="未稅金額">{formatNumber(data.fc120_20)}</Descriptions.Item>
            <Descriptions.Item label="含稅總額">{formatNumber(data.fc120_201)}</Descriptions.Item>
            <Descriptions.Item label="稅額">{formatNumber(data.fc120_16)}</Descriptions.Item>
            <Descriptions.Item label="明細行數">{data.fc120_27}</Descriptions.Item>
            <Descriptions.Item label="審核狀態">{renderApprovalStat(data.fc120_80)}</Descriptions.Item>
          </Descriptions>
        </div>
        <div className="c504-detail-section">
          <h4>產品明細 ({data.details?.length || 0} 項)</h4>
          <Table<FC121Detail>
            rowKey={(r) => `${r.fc121_01}-${r.fc121_02}`}
            columns={detailColumns}
            dataSource={data.details || []}
            size="small"
            pagination={false}
            scroll={{ x: 700 }}
          />
        </div>
      </>
    ) : (
      <Text type="secondary">無資料</Text>
    )}
  </Drawer>
);

export default DetailDrawer;
